import axios from 'api/axios'
import {
	MessagingPrice,
	PricingFilters,
	CreatePricingData,
	UpdatePricingData,
	BulkPricingUpdate,
} from 'types'

export interface PricingResponse {
	records: MessagingPrice[]
	totalCount: number
	currentPage: number
	totalPages: number
}

export interface PricingApiResponse {
	success: boolean
	data: MessagingPrice[]
	message?: string
}

export const pricingService = {
	/**
	 * Get pricing data with filters and pagination
	 */
	getPricing: async (
		filters: PricingFilters & { page?: number; size?: number } = {}
	): Promise<PricingResponse> => {
		const response = await axios.get(`pricing`, {
			params: {
				...filters,
				page: filters.page || 1,
				size: filters.size || 20,
			},
		})
		return response.data
	},

	/**
	 * Get all pricing data without pagination (for exports)
	 */
	getAllPricing: async (
		filters: PricingFilters = {}
	): Promise<MessagingPrice[]> => {
		const response = await axios.get(`pricing`, {
			params: filters,
		})
		return response.data
	},

	/**
	 * Create new pricing entry
	 */
	createPricing: async (
		data: CreatePricingData
	): Promise<PricingApiResponse> => {
		const response = await axios.post(`pricing`, data)
		return response.data
	},

	/**
	 * Update existing pricing entry
	 */
	updatePricing: async (
		id: string,
		data: UpdatePricingData
	): Promise<PricingApiResponse> => {
		const response = await axios.put(`pricing/${id}`, data)
		return response.data
	},

	/**
	 * Delete pricing entry
	 */
	deletePricing: async (id: string): Promise<PricingApiResponse> => {
		const response = await axios.delete(`pricing/${id}`)
		return response.data
	},

	/**
	 * Bulk update pricing entries
	 */
	bulkUpdatePricing: async (
		updates: BulkPricingUpdate[]
	): Promise<PricingApiResponse> => {
		const response = await axios.post(`pricing/bulk-update`, {
			updates,
		})
		return response.data
	},

	/**
	 * Import pricing data from CSV
	 */
	importPricing: async (file: File): Promise<PricingApiResponse> => {
		const formData = new FormData()
		formData.append('file', file)

		const response = await axios.post(`pricing/import`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})
		return response.data
	},

	/**
	 * Export pricing data to CSV
	 */
	exportPricing: async (filters: PricingFilters = {}): Promise<Blob> => {
		const response = await axios.get(`pricing/export`, {
			params: filters,
			responseType: 'blob',
		})
		return response.data
	},

	/**
	 * Seed sample pricing data (admin utility)
	 */
	seedSampleData: async (): Promise<PricingApiResponse> => {
		const response = await axios.post(`pricing/seed-sample-data`)
		return response.data
	},

	/**
	 * Get unique countries for filter dropdown
	 */
	getCountries: async (): Promise<
		Array<{ code: string; name: string; phone_code: string }>
	> => {
		const response = await axios.get(`pricing/countries`)
		return response.data
	},

	/**
	 * Duplicate pricing for different vendor/currency
	 */
	duplicatePricing: async (
		sourceId: string,
		targetVendor: string,
		targetCurrency: string
	): Promise<PricingApiResponse> => {
		const response = await axios.post(`pricing/duplicate`, {
			sourceId,
			targetVendor,
			targetCurrency,
		})
		return response.data
	},
}

export default pricingService
